import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { ProfileFormData } from "@/pages/ProfileCreation";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import {
  SECT,
  MASJID_ATTENDANCE,
  PRAYER_FREQUENCY,
  HIJAB_LEVEL,
  BEARD_LEVEL,
  QURAN_KNOWLEDGE,
  ISLAMIC_KNOWLEDGE_LEVEL,
} from "@/lib/enums";

const createSchema = (isFemale: boolean) => z.object({
  importance_of_islam: z.number().min(1, "Islam importance must be at least 1").max(10, "Islam importance must be 10 or less"),
  sect: z.string().min(1, "Sect is required"),
  masjid_attendance: z.string().min(1, "Masjid attendance is required"),
  prayer_frequency: z.string().min(1, "Prayer frequency is required"),
  wears_hijab_level: isFemale ? z.string().min(1, "Hijab level is required") : z.string().optional(),
  has_beard: !isFemale ? z.string().min(1, "Beard status is required") : z.string().optional(),
  quran_knowledge: z.string().min(1, "Quran knowledge is required"),
  islamic_knowledge_level: z.string().min(1, "Islamic knowledge level is required"),
});

// SSoT enums are ordered low → high (so the algorithm can treat the index as a
// rank). The form displays them high → low so users see the most-religious
// option first, matching the previous UX. We reverse() at render time to keep
// SSoT as the single source of truth without forking ordering semantics.
const SECT_OPTIONS = SECT;
const MASJID_ATTENDANCE_OPTIONS = [...MASJID_ATTENDANCE].reverse();
const PRAYER_FREQUENCY_OPTIONS = [...PRAYER_FREQUENCY].reverse();
const HIJAB_LEVEL_OPTIONS = [...HIJAB_LEVEL].reverse();
const BEARD_LEVEL_OPTIONS = [...BEARD_LEVEL].reverse();
const QURAN_KNOWLEDGE_OPTIONS = [...QURAN_KNOWLEDGE].reverse();
const ISLAMIC_KNOWLEDGE_OPTIONS = [...ISLAMIC_KNOWLEDGE_LEVEL].reverse();

interface Props {
  data: ProfileFormData;
  onComplete: (data: Partial<ProfileFormData>) => void;
  onBack: () => void;
  saving: boolean;
  continueLabel?: string;
  loadingLabel?: string;
}

export function Step2ReligiousProfile({ data, onComplete, onBack, saving, continueLabel = "Save & Continue", loadingLabel = "Saving..." }: Props) {
  const isFemale = data.gender === "female";
  const isMale = data.gender === "male";

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createSchema(isFemale)),
    defaultValues: {
      importance_of_islam: data.importance_of_islam || 5,
      sect: data.sect,
      masjid_attendance: data.masjid_attendance,
      prayer_frequency: data.prayer_frequency,
      wears_hijab_level: data.wears_hijab_level,
      has_beard: data.has_beard,
      quran_knowledge: data.quran_knowledge,
      islamic_knowledge_level: data.islamic_knowledge_level,
    },
  });

  // Sync form with prop changes (e.g., from Quick Import)
  useEffect(() => {
    reset({
      importance_of_islam: data.importance_of_islam || 5,
      sect: data.sect,
      masjid_attendance: data.masjid_attendance,
      prayer_frequency: data.prayer_frequency,
      wears_hijab_level: data.wears_hijab_level,
      has_beard: data.has_beard,
      quran_knowledge: data.quran_knowledge,
      islamic_knowledge_level: data.islamic_knowledge_level,
    });
  }, [data, reset]);

  const importanceOfIslam = watch("importance_of_islam");

  const getIslamImportanceLabel = (value: number) => {
    if (value <= 3) return "Exploring";
    if (value <= 7) return "Moderate";
    return "Central to everything";
  };

  const onSubmit = (formData: any) => {
    onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Faith & Practice</h3>
        
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label>How important is Islam in your life? *</Label>
            <span className="text-sm font-medium text-primary">
              {importanceOfIslam} - {getIslamImportanceLabel(importanceOfIslam)}
            </span>
          </div>
          <Slider
            value={[importanceOfIslam]}
            onValueChange={(value) => setValue("importance_of_islam", value[0])}
            min={1}
            max={10}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 - Exploring</span>
            <span>5 - Moderate</span>
            <span>10 - Central</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sect">Sect/School of Thought *</Label>
          <Select value={watch("sect")} onValueChange={(value) => setValue("sect", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your sect" />
            </SelectTrigger>
            <SelectContent>
              {SECT_OPTIONS.map((sect) => (
                <SelectItem key={sect} value={sect}>{sect}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.sect && (
            <p className="text-sm text-destructive">{errors.sect.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="masjid_attendance">How often do you attend the masjid? *</Label>
          <Select value={watch("masjid_attendance")} onValueChange={(value) => setValue("masjid_attendance", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              {MASJID_ATTENDANCE_OPTIONS.map((freq) => (
                <SelectItem key={freq} value={freq}>{freq}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.masjid_attendance && (
            <p className="text-sm text-destructive">{errors.masjid_attendance.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="prayer_frequency">Prayer consistency *</Label>
          <Select value={watch("prayer_frequency")} onValueChange={(value) => setValue("prayer_frequency", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your prayer consistency" />
            </SelectTrigger>
            <SelectContent>
              {PRAYER_FREQUENCY_OPTIONS.map((freq) => (
                <SelectItem key={freq} value={freq}>{freq}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.prayer_frequency && (
            <p className="text-sm text-destructive">{errors.prayer_frequency.message}</p>
          )}
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Religious Appearance</h3>
        
        {isFemale && (
          <div className="space-y-2">
            <Label htmlFor="wears_hijab_level">Do you wear hijab? *</Label>
            <Select value={watch("wears_hijab_level")} onValueChange={(value) => setValue("wears_hijab_level", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select your hijab practice" />
              </SelectTrigger>
              <SelectContent>
                {HIJAB_LEVEL_OPTIONS.map((level) => (
                  <SelectItem key={level} value={level}>{level}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.wears_hijab_level && (
              <p className="text-sm text-destructive">{errors.wears_hijab_level.message}</p>
            )}
          </div>
        )}

        {isMale && (
          <div className="space-y-2">
            <Label htmlFor="has_beard">Beard status? *</Label>
            <Select value={watch("has_beard")} onValueChange={(value) => setValue("has_beard", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select your beard status" />
              </SelectTrigger>
              <SelectContent>
                {BEARD_LEVEL_OPTIONS.map((level) => (
                  <SelectItem key={level} value={level}>{level}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.has_beard && (
              <p className="text-sm text-destructive">{errors.has_beard.message}</p>
            )}
          </div>
        )}
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Islamic Knowledge</h3>
        
        <div className="space-y-2">
          <Label htmlFor="quran_knowledge">Quran knowledge *</Label>
          <Select value={watch("quran_knowledge")} onValueChange={(value) => setValue("quran_knowledge", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your Quran knowledge level" />
            </SelectTrigger>
            <SelectContent>
              {QURAN_KNOWLEDGE_OPTIONS.map((level) => (
                <SelectItem key={level} value={level}>{level}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.quran_knowledge && (
            <p className="text-sm text-destructive">{errors.quran_knowledge.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="islamic_knowledge_level">Islamic knowledge level *</Label>
          <Select value={watch("islamic_knowledge_level")} onValueChange={(value) => setValue("islamic_knowledge_level", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your knowledge level" />
            </SelectTrigger>
            <SelectContent>
              {ISLAMIC_KNOWLEDGE_OPTIONS.map((level) => (
                <SelectItem key={level} value={level}>{level}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.islamic_knowledge_level && (
            <p className="text-sm text-destructive">{errors.islamic_knowledge_level.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-between gap-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" disabled={saving} className="min-w-32">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {loadingLabel}
            </>
          ) : (
            continueLabel
          )}
        </Button>
      </div>
    </form>
  );
}
